import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  DmAccountCard,
  DmDropdown,
  DmDropdownContent,
  DmDropdownItem,
  DmDropdownRadioGroup,
  DmDropdownSub,
  DmDropdownSubTrigger,
} from '@digitale-menschen/component-library-angular';
import { API_CONFIGURATION } from '@lexplus-environments';
import { ProfileState } from '../../states/profile/profile-state';
import { normalizeLanguage } from '../../functions/supported-language.function';
import { ColorSchemeChoice, ColorSchemeService } from '../../services/color-scheme.service';
import { LanguageService } from '../../services/language.service';
import { LocalizedRouteService } from '../../services/localized-route.service';
import { LOCALIZED_SHARED_ROUTES, SHARED_ROUTE_IDS } from '../../shared-route-const';
import { LanguageInterface } from '../../models/language.interface';

/**
 * Account card in the sidebar footer that opens a dropdown menu (profile,
 * language switch, the Neosys-only colour scheme, logout). The card and language handling are self-contained;
 * logout is delegated to the host app via the `logout` output because the auth
 * routes live outside the shared library.
 */
@Component({
  selector: 'lp-account-menu',
  imports: [DmAccountCard, DmDropdown, DmDropdownContent, DmDropdownItem, DmDropdownRadioGroup, DmDropdownSub, DmDropdownSubTrigger],
  templateUrl: './account-menu.component.html',
  styleUrl: './account-menu.component.scss',
  host: {
    '[class.lp-account-menu--on-brand]': "appearance() === 'on-brand'",
    '[class.lp-account-menu--collapsed]': 'collapsed()',
  },
})
export class AccountMenuComponent {
  /** `on-brand` styles the card for a brand-coloured sidebar surface. */
  public readonly appearance = input<'default' | 'on-brand'>('default');
  /** When the sidebar is collapsed to the icon rail, the full account card
   * doesn't fit — show just the avatar as the dropdown trigger instead. */
  public readonly collapsed = input<boolean>(false);
  public readonly logout = output<void>();

  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly localizedRouteService = inject(LocalizedRouteService);
  private readonly languageService = inject(LanguageService);
  private readonly localeId = inject(LOCALE_ID);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  protected readonly colorSchemeService = inject(ColorSchemeService);

  protected readonly languages = signal<LanguageInterface[]>(this.languageService.getDefaultLanguagesWithLabel());
  protected readonly profile = this.store.selectSignal(ProfileState.profile);
  /** Which radio is selected — the language this bundle was built for. */
  protected readonly currentLanguage = normalizeLanguage(this.localeId) ?? 'de';

  protected readonly colorSchemeOptions: readonly { value: ColorSchemeChoice; label: string; icon: string }[] = [
    { value: 'light', label: $localize`:@@account.menu.colorScheme.light:Hell`, icon: 'sun' },
    { value: 'dark', label: $localize`:@@account.menu.colorScheme.dark:Dunkel`, icon: 'moon-01' },
    { value: 'system', label: $localize`:@@account.menu.colorScheme.system:System`, icon: 'monitor-01' },
  ];
  /** The trigger shows the icon of the current choice. */
  protected readonly colorSchemeIcon = computed(
    () => this.colorSchemeOptions.find((option) => option.value === this.colorSchemeService.choice())?.icon ?? 'sun',
  );

  protected readonly accountName = computed(() => {
    const profile = this.profile();
    return [profile?.vorname, profile?.nachname].filter(Boolean).join(' ') || profile?.userName || '';
  });

  protected readonly email = computed(() => {
    const profile = this.profile();
    return profile?.userName;
  });

  protected readonly accountInitials = computed(() => {
    const profile = this.profile();
    const initials = [profile?.vorname, profile?.nachname]
      .filter(Boolean)
      .map((part) => part?.[0])
      .join('');
    return initials || profile?.userName?.[0] || '';
  });

  /**
   * The user's own picture, or `null` so the card falls back to the initials.
   *
   * It comes from the authentication API, which both apps share, and the browser fetches the
   * `<img>` itself, so no interceptor sees it; the session cookie rides along.
   */
  protected readonly imgsrc = computed(() => {
    const profilbildUrl = this.profile()?.profilbildUrl;
    return profilbildUrl ? `${this.apiConfiguration.baseUrlAuth}${profilbildUrl}` : null;
  });

  protected switchLanguage(code: string): void {
    if (code === this.currentLanguage) {
      return;
    }
    // Through the LanguageService, not straight to the route service: the choice
    // has to reach the account and the cookie first, or the next page load reads
    // the old language back and redirects the user right back. Not awaited — the
    // service redirects once it has persisted, and a failure raises its own toast.
    void this.languageService.changeLanguage(code);
  }

  protected switchColorScheme(choice: string): void {
    const option = this.colorSchemeOptions.find((candidate) => candidate.value === choice);
    if (!option) {
      return;
    }
    this.colorSchemeService.setChoice(option.value);
  }

  protected goToProfile(): void {
    const profilePath = this.localizedRouteService.getLocalizedRoute(LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.PROFILE]);
    void this.router.navigateByUrl(`/${profilePath}`);
  }
}
